import { Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/firestore";

export interface CustomerAddress {
  address: string;
  pincode: string;
  locality: string;
  landmark: string;
}

export interface GtgCustomerDetail {
  name: string;
  phonenumber: number;
  addresses: CustomerAddress[];
}

@Injectable({ providedIn: "root" })
export class CustomerAddressService {
  constructor(private afs: AngularFirestore) {}

  // Create a new customer with an initial address
  createCustomer(
    name: string,
    phonenumber: number,
    customerAddress: CustomerAddress
  ): Promise<void> {
    const docId = phonenumber.toString();
    const customerDetail: GtgCustomerDetail = {
      name,
      phonenumber,
      addresses: [customerAddress],
    };
    return this.afs
      .collection(`customer_address`)
      .doc(docId)
      .set(customerDetail);
  }

  // Add an address to an existing customer
  addCustomerAddress(
    customerAddress: CustomerAddress,
    phonenumber: number
  ): Promise<void> {
    const docId = phonenumber.toString();
    const customerDocRef = this.afs.collection("customer_address").doc(docId);

    return this.afs.firestore.runTransaction(async (transaction) => {
      const doc = await transaction.get(customerDocRef.ref);
      if (!doc.exists) {
        throw new Error("Document does not exist!");
      }

      const customerDetail = doc.data() as GtgCustomerDetail;
      const updatedAddresses = [...customerDetail.addresses, customerAddress];

      transaction.update(customerDocRef.ref, { addresses: updatedAddresses });
    });
  }

  // Get all addresses for a given phone number
  getCustomerAddresses(
    phonenumber: number
  ): Promise<GtgCustomerDetail | undefined> {
    const docId = phonenumber.toString();
    return this.afs
      .doc<GtgCustomerDetail>(`customer_address/${docId}`)
      .get()
      .toPromise()
      .then((doc) => {
        return doc.exists ? (doc.data() as GtgCustomerDetail) : undefined;
      });
  }

  // Delete an address by ID from an existing customer
  deleteCustomerAddress(addressId: number, phonenumber: number): Promise<void> {
    const docId = phonenumber.toString();
    const customerDocRef = this.afs.collection("customer_address").doc(docId);

    return this.afs.firestore.runTransaction(async (transaction) => {
      const doc = await transaction.get(customerDocRef.ref);
      if (!doc.exists) {
        throw new Error("Document does not exist!");
      }

      const customerDetail = doc.data() as GtgCustomerDetail;
      const updatedAddresses = customerDetail.addresses.filter(
        (_: CustomerAddress, index: number) => index !== addressId
      );

      transaction.update(customerDocRef.ref, { addresses: updatedAddresses });
    });
  }
}
